import { Fragment, useEffect, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { CheckIcon, LockClosedIcon, TagIcon } from '@heroicons/react/20/solid'
import { useSession } from "next-auth/react"
import { useRouter } from 'next/router'
import axios from 'axios'
import { plans, premiumPlusPlan, isPurchasable } from '@/lib/config'
import { pricingPreview, DEFAULT_CREDITS_DETAIL } from '@/lib/pricingPreview'
import type { Plan } from '@/types'
import type { CouponCheckResponse } from '@/pages/api/stripe/coupon'

import { useUser } from '@/contexts/UserContext'
import { useUpgrade } from '@/contexts/UpgradeContext'
import { createLogger } from '@/utils/logger';
import PricingTestimonialsMarquee from './PricingTestimonialsMarquee'

const logger = createLogger('UpgradeModal');

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

const PROFESSIONAL_PLAN_IDS = new Set(['pro', 'pro-plus'])

// Marketing coupon (from /upgrade?coupon_id=) pre-flight state. The discount
// is only sent to checkout once Stripe has confirmed the coupon is still valid.
type CouponStatus =
  | { state: 'none' }
  | { state: 'checking' }
  | { state: 'valid'; percentOff: number }
  | { state: 'invalid' }

// Stripe applies the percentage to the exact price, so show cents when the
// discounted amount isn't whole ($19 -> $9.50).
const formatPrice = (amount: number) =>
  Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`

// Standardized card styling with Premium as the clear recommendation.
const basePresentation = {
  pill: null,
  card: 'border border-gray-200 bg-white shadow-sm',
  title: 'text-gray-900',
  price: 'text-gray-950',
  button: 'bg-blue-600 text-white shadow-sm hover:bg-blue-700',
  check: 'text-blue-600',
  pillClass: 'bg-blue-600 text-white ring-blue-600',
}

const planPresentation: Record<string, typeof basePresentation> = {
  starter: {
    ...basePresentation,
  },
  premium: {
    ...basePresentation,
    pill: 'Most Popular',
    card: 'border border-blue-200 bg-white ring-2 ring-blue-600 shadow-xl shadow-blue-500/10',
  },
  pro: {
    ...basePresentation,
  },
  'pro-plus': {
    ...basePresentation,
  },
  'premium-plus': {
    ...basePresentation,
  },
}

// Presentation overrides — Premium is "Most Popular"
const getPresentation = (planId: string) => {
  return planPresentation[planId] || planPresentation.starter
}

interface UpgradeModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  onUpgradeSuccess?: () => void;
  upgradeReason?: string | null;
  /** Caller-composed concrete line (e.g. the exact address Premium+ unlocks);
   * shown above the plan grid, stacked with any reason banner. */
  upgradeHighlight?: string | null;
}

export default function UpgradeModal({
  open,
  setOpen,
  onUpgradeSuccess,
  upgradeReason = null,
  upgradeHighlight = null,
}: UpgradeModalProps) {
  const { data: session } = useSession()
  const { user } = useUser()
  const { upgradeCouponId } = useUpgrade()
  const router = useRouter()
  const [frequency, setFrequency] = useState('yearly')
  const [showProfessionalPlans, setShowProfessionalPlans] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  // Result of the last coupon pre-flight, keyed by coupon id so a stale answer
  // for a previous coupon is never shown for the current one.
  const [couponCheck, setCouponCheck] = useState<{ id: string; status: CouponStatus } | null>(null)
  const coupon: CouponStatus = !upgradeCouponId
    ? { state: 'none' }
    : couponCheck?.id === upgradeCouponId
      ? couponCheck.status
      : { state: 'checking' }

  // Validate the marketing coupon when the modal opens with one. An expired or
  // already-used coupon is reported here, above the plans, instead of being
  // discovered inside Stripe Checkout.
  useEffect(() => {
    if (!open || !upgradeCouponId) return
    const id = upgradeCouponId
    let cancelled = false
    axios
      .get<CouponCheckResponse>('/api/stripe/coupon', { params: { id } })
      .then((res) => {
        if (cancelled) return
        const { valid, percent_off } = res.data
        const status: CouponStatus =
          valid && typeof percent_off === 'number' && percent_off > 0
            ? { state: 'valid', percentOff: percent_off }
            : { state: 'invalid' }
        setCouponCheck({ id, status })
      })
      .catch((error) => {
        if (cancelled) return
        logger.warn('Coupon check failed; showing regular prices', { error })
        setCouponCheck({ id, status: { state: 'invalid' } })
      })
    return () => {
      cancelled = true
    }
  }, [open, upgradeCouponId])

  const discountPercent = coupon.state === 'valid' ? coupon.percentOff : 0
  const applyDiscount = (amount: number) =>
    discountPercent > 0 ? Math.round(amount * (100 - discountPercent)) / 100 : amount


  // Treat the user as "already subscribed" when the UserContext carries a
  // confirmed paid plan. We intentionally require BOTH signals (`subscribed`
  // and `plan !== 'free'`) so an inconsistent state (e.g. mid-refund) still
  // lets the user recover by re-subscribing.
  const hasActiveSubscription = !!user && user.subscribed === true && user.plan && user.plan !== 'free'
  // Reason-specific banner above the plan grid. Reasons are free-form strings
  // passed to showUpgrade(); the email-forwarding surfaces use these three
  // (workspace email address model: docs/plans/workspace-email-address.md).
  const REASON_BANNERS: Record<string, string> = {
    // email_forwarding_locked deliberately has NO banner (2026-08-21): the
    // general email paywall should present the plain plan grid so users read
    // the pricing itself. Feature-specific gates below keep their tier hint.
    // invite_users also has NO banner: the pricing cards themselves say
    // "Invite 2 users to work in your books" — let people read the cards.
    collaborators: 'Inviting senders is included from Premium+',
    connect_mailbox: 'Connected mailboxes are included from Premium',
    rename_address: 'Choosing your own address name is included from Premium+',
  }
  const reasonBanner = upgradeReason ? REASON_BANNERS[upgradeReason] ?? null : null

  // Build the final list: all plans + mock Premium+ (ordered: starter → premium → premium+ → pro → pro+).
  // Starter is always shown (including the auto-forwarding paywall) for a consistent
  // three-plan lineup that matches onboarding.
  const order = ['starter', 'premium', 'premium-plus', 'pro', 'pro-plus']
  const allDisplayPlans = order
    .map((id) => {
      const plan = plans.find((p) => p.id === id)
      if (plan) return plan
      if (id === 'premium-plus') return premiumPlusPlan
      return null
    })
    .filter((p): p is Plan => p !== null)
  const displayPlans = allDisplayPlans.filter((plan) =>
    showProfessionalPlans
      ? PROFESSIONAL_PLAN_IDS.has(plan.id)
      : !PROFESSIONAL_PLAN_IDS.has(plan.id)
  )

  const handleClose = () => {
    setOpen(false)
  }

  const goToManageSubscription = () => {
    handleClose()
    router.push('/?settings=true&tab=Subscription')
  }

  const handleCheckout = async (plan) => {
    if (plan === "free") {
      handleClose()
      return
    }

    // Defence-in-depth: even if the UI button is hidden for active
    // subscribers, route them to Settings rather than firing a checkout
    // request that will now come back as 409.
    if (hasActiveSubscription) {
      goToManageSubscription()
      return
    }

    // Defence-in-depth alongside the disabled CTA: never open a checkout session
    // for a plan whose Stripe price id is still empty.
    const target = allDisplayPlans.find((candidate) => candidate.id === plan)
    if (target && !isPurchasable(target, frequency as 'monthly' | 'yearly')) {
      return
    }

    setIsLoading(true)
    
    try {
      const url = process.env.NEXT_PUBLIC_HOST_URL + '/api/stripe/checkout'
      const res = await axios.post(url, { 
        'user': session.user.id, 
        'plan': plan, 
        'email': session.user.email,
        'frequency': frequency,
        // Pre-apply the marketing coupon only once Stripe confirmed it is valid.
        ...(coupon.state === 'valid' && upgradeCouponId ? { 'coupon_id': upgradeCouponId } : {}),
      })
      
      // Close modal and redirect to checkout
      handleClose()
      window.location.href = res.data.checkout_url
      
      // Optionally call success callback
      if (onUpgradeSuccess) {
        onUpgradeSuccess()
      }
    } catch (error: any) {
      logger.error('Error initiating checkout:', { error });
      // 1.7 — handle the new 409 `subscription_exists` response from
      // /api/stripe/checkout. The server returns this when the user
      // already has an active-ish Stripe subscription, to prevent the
      // double-upgrade / duplicate-customer class of bugs.
      const status = error?.response?.status
      const code = error?.response?.data?.error
      if (status === 409 && code === 'subscription_exists') {
        const message = error?.response?.data?.message
          || 'You already have an active subscription. Manage your plan from Settings → Subscription.'
        alert(message)
        goToManageSubscription()
      } else if (status === 400 && code === 'coupon_invalid') {
        // Validated fine a moment ago but Stripe rejected it at session time
        // (e.g. redeemed in another tab). Drop the offer; the user can retry
        // at the regular price.
        if (upgradeCouponId) setCouponCheck({ id: upgradeCouponId, status: { state: 'invalid' } })
        alert(error?.response?.data?.message || 'This offer is no longer valid.')
      } else {
        alert('There was an error processing your request. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[1000]" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full max-w-7xl transform overflow-hidden rounded-2xl bg-gray-50 px-4 pb-5 pt-5 text-left shadow-2xl transition-all sm:my-8 sm:p-8">
                {/* Header with Close Button */}
                <div className="absolute right-0 top-0 pr-4 pt-4">
                  <button
                    type="button"
                    className="rounded-md bg-white/80 backdrop-blur-sm text-gray-400 hover:text-gray-500 hover:bg-white/90 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="py-6">
                  <div className='mx-auto w-full max-w-[95%]'>
                    {(upgradeHighlight || reasonBanner) && (
                      <div className="mx-auto mb-4 flex max-w-xl items-center justify-center gap-2 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-center text-orange-700 shadow-sm">
                        <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-amber-50 to-amber-200 text-amber-800 shadow-md ring-2 ring-white">
                          <LockClosedIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                        <div>
                          {upgradeHighlight && <p className="text-sm font-semibold">{upgradeHighlight}</p>}
                          {reasonBanner && <p className="text-sm font-semibold">{reasonBanner}</p>}
                        </div>
                      </div>
                    )}
                    
                    {coupon.state === 'valid' && (
                      <div className="mx-auto mb-4 flex max-w-xl items-center justify-center gap-2 rounded-2xl border border-green-100 bg-green-50 px-4 py-3 text-center text-green-800 shadow-sm">
                        <TagIcon className="h-5 w-5 flex-shrink-0 text-green-600" aria-hidden="true" />
                        <p className="text-sm font-semibold">
                          {coupon.percentOff}% off any plan is applied at checkout, for as long as you stay subscribed.
                        </p>
                      </div>
                    )}
                    {coupon.state === 'invalid' && (
                      <div className="mx-auto mb-4 flex max-w-xl items-center justify-center gap-2 rounded-2xl border border-amber-100 bg-amber-50 px-4 py-3 text-center text-amber-800 shadow-sm">
                        <TagIcon className="h-5 w-5 flex-shrink-0 text-amber-600" aria-hidden="true" />
                        <p className="text-sm font-semibold">
                          This offer has expired. The regular prices are shown below.
                        </p>
                      </div>
                    )}

                    <div className="mx-auto max-w-2xl pt-4 text-center">
                      <h1 className="text-3xl font-bold tracking-tight text-gray-950">Choose the plan that fits your workflow</h1>
                    </div>

                    <div className="mt-4 flex flex-col items-center space-y-2">
                      <div className="relative flex rounded-full p-1 bg-gray-100">
                        <button
                          onClick={() => setFrequency("monthly")}
                          type="button"
                          className={classNames(
                            frequency === "monthly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-700 hover:bg-gray-50",
                            "relative rounded-full py-1.5 px-5 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                          )}
                        >
                          Monthly
                        </button>
                        <button
                          onClick={() => setFrequency("yearly")}
                          type="button"
                          className={classNames(
                            frequency === "yearly" ? "bg-white text-gray-900 shadow-sm" : "text-gray-700 hover:bg-gray-50",
                            "relative rounded-full py-1.5 px-5 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                          )}
                        >
                          Annual <span className={classNames(
                            frequency === "yearly" ? "text-green-500" : "text-gray-500",
                            "text-xs"
                          )}>Save up to 33%</span>
                        </button>
                      </div>
                    </div>

                    <div className={classNames(
                      'isolate mx-auto mt-7 grid w-full grid-cols-1 gap-5 sm:grid-cols-2',
                      showProfessionalPlans ? 'max-w-4xl lg:grid-cols-2' : 'max-w-6xl lg:grid-cols-3'
                    )}>
                      {displayPlans.map((plan) => {
                        const monthlyPriceNum = plan.prices.monthly.amount;
                        const originalAnnualPrice = monthlyPriceNum * 12;
                        const discountedMonthly = applyDiscount(plan.prices.monthly.amount);
                        const discountedYearly = applyDiscount(plan.prices.yearly.amount);
                        const presentation = getPresentation(plan.id);
                        // A plan whose Stripe price isn't wired up yet (Premium+) must not
                        // open a checkout session — it would fail against an empty price id.
                        const purchasable = isPurchasable(plan, frequency as 'monthly' | 'yearly');
                        const planIndex = displayPlans.findIndex((candidatePlan) => candidatePlan.id === plan.id);
                        const previousPlan = planIndex > 0 ? displayPlans[planIndex - 1] : null;
                        const preview = pricingPreview[plan.id] || {
                          description: plan.description,
                          outcome: '',
                          monthlyCredits: String(plan.prices.monthly.credits),
                          creditsDetail: DEFAULT_CREDITS_DETAIL,
                          features: plan.features.map((feature) => ({
                            title: feature,
                            detail: '',
                          })),
                        };

                        return (
                          <div
                            key={plan.id}
                            className={classNames(
                              presentation.card,
                              'relative flex h-full flex-col rounded-xl p-6 transition-shadow duration-200 hover:shadow-lg xl:p-7'
                            )}
                          >
                            <div className="flex items-center justify-between gap-x-4">
                              <h3
                                id={plan.id}
                                className={classNames(presentation.title, 'text-xl font-semibold leading-8')}
                              >
                                {plan.name}
                              </h3>
                              {presentation.pill && (
                                <p className={classNames(
                                  presentation.pillClass,
                                  'rounded-full whitespace-nowrap px-2.5 py-1 text-xs font-semibold leading-5 ring-1 ring-inset'
                                )}>
                                  {presentation.pill}
                                </p>
                              )}
                            </div>
                            <div className="mt-3 flex items-end gap-2">
                              {frequency === 'monthly' ? (
                                <>
                                  <span className={classNames(presentation.price, 'text-[2.5rem] font-bold leading-none tracking-tight')}>
                                    {discountPercent > 0 ? formatPrice(discountedMonthly) : plan.prices.monthly.display}
                                  </span>
                                  <div className="pb-1 text-xs leading-4 text-gray-600">
                                    <span className="block font-semibold">per month</span>
                                    {discountPercent > 0 && (
                                      <span className="block text-gray-500">
                                        <s>{plan.prices.monthly.display}</s> with {discountPercent}% off
                                      </span>
                                    )}
                                  </div>
                                </>
                              ) : (
                                <>
                                  <span className={classNames(presentation.price, 'text-[2.5rem] font-bold leading-none tracking-tight')}>
                                    ${Math.round(discountedYearly / 12)}
                                  </span>
                                  <div className="pb-0.5 text-xs leading-4 text-gray-500">
                                    <span className="block font-semibold text-gray-700">per month</span>
                                    <span className="block">
                                      Billed {formatPrice(discountedYearly)} annually
                                      {discountPercent > 0 && <> (<s>${plan.prices.yearly.amount}</s>)</>}
                                    </span>
                                    <span className="block text-green-600">
                                      ~{Math.round((originalAnnualPrice - plan.prices.yearly.amount) / plan.prices.monthly.amount)} months free
                                    </span>
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="mt-4 min-h-[76px] text-sm leading-6">
                              <p className="font-semibold text-gray-800">{preview.description}</p>
                              <p className="mt-1 text-gray-600">{preview.outcome}</p>
                            </div>
                            <button
                              onClick={() => hasActiveSubscription ? goToManageSubscription() : handleCheckout(plan.id)}
                              aria-describedby={plan.id}
                              disabled={isLoading || (!purchasable && !hasActiveSubscription)}
                              className={classNames(
                                presentation.button,
                                'mt-6 block w-full rounded-xl px-3 py-3 text-center text-sm font-semibold leading-6 transition-all duration-200 hover:scale-[1.02] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:transform-none disabled:hover:scale-100'
                              )}
                            >

                              {isLoading
                                ? 'Processing...'
                                : plan.disabled
                                  ? 'Continue with free plan'
                                  : hasActiveSubscription
                                    ? 'Manage subscription →'
                                    : !purchasable
                                      ? 'Coming soon'
                                      : 'Upgrade →'}
                            </button>
                            <div className="mt-8 flex-1 xl:mt-10">
                              <p className="mb-3 text-sm font-semibold text-gray-800">
                                {previousPlan ? `Everything in ${previousPlan.name}, plus:` : 'Including:'}
                              </p>
                              <ul role="list" className="space-y-3 text-sm leading-6 text-gray-600">
                                <li className="flex gap-x-3">
                                  <CheckIcon
                                    className={classNames(presentation.check, 'h-6 w-5 flex-none')}
                                    aria-hidden="true"
                                  />
                                  <span>
                                    <span className="block font-semibold text-gray-800">{preview.monthlyCredits} credits each month</span>
                                    <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                                      {preview.creditsDetail}
                                    </span>
                                  </span>
                                </li>
                                {preview.features.map((feature) => (
                                  <li
                                    key={feature.title}
                                    className="flex gap-x-3"
                                  >
                                    <CheckIcon
                                      className={classNames(presentation.check, 'h-6 w-5 flex-none')}
                                      aria-hidden="true"
                                    />
                                    <span>
                                      <span className="block font-semibold text-gray-800">{feature.title}</span>
                                      {feature.detail && (
                                        <span className="mt-0.5 block text-xs leading-5 text-gray-500">
                                          {feature.detail}
                                        </span>
                                      )}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    <div className="mx-auto mt-8 flex max-w-3xl flex-col items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white/80 px-5 py-4 text-center shadow-sm sm:flex-row sm:text-left">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Are you a bookkeeping professional?</p>
                        <p className="mt-1 text-xs leading-5 text-gray-500">
                          {showProfessionalPlans
                            ? 'Showing plans for bookkeepers, accountants, and high-volume teams.'
                            : 'Switch to plans designed for managing more clients and documents.'}
                        </p>
                      </div>
                      <div
                        className="relative flex shrink-0 rounded-full bg-gray-100 p-1"
                        role="group"
                        aria-label="Choose plans for your type of work"
                      >
                        <button
                          type="button"
                          aria-pressed={!showProfessionalPlans}
                          onClick={() => setShowProfessionalPlans(false)}
                          className={classNames(
                            !showProfessionalPlans ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900',
                            'relative rounded-full px-4 py-2 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                          )}
                        >
                          Personal plans
                        </button>
                        <button
                          type="button"
                          aria-pressed={showProfessionalPlans}
                          onClick={() => setShowProfessionalPlans(true)}
                          className={classNames(
                            showProfessionalPlans ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900',
                            'relative rounded-full px-4 py-2 text-sm font-medium transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600'
                          )}
                        >
                          Pro plans
                        </button>
                      </div>
                    </div>

                    {/* Trust signals */}
                    <div className="mt-8 text-center">
                      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
                        <p className="flex items-center">
                          <span className="text-blue-600 text-lg mr-2">✓</span> 
                          <span className="font-medium text-gray-700 text-sm">All major credit cards accepted</span>
                        </p>
                        <p className="flex items-center">
                          <span className="text-blue-600 text-lg mr-2">✓</span> 
                          <span className="font-medium text-gray-700 text-sm">30-day money-back guarantee</span>
                        </p>
                        <p className="flex items-center">
                          <span className="text-blue-600 text-lg mr-2">✓</span> 
                          <span className="font-medium text-gray-700 text-sm">Unsubscribe at any time</span>
                        </p>
                      </div>
                      <p className="mt-4 text-xs text-gray-600">
                        Join over 300+ businesses and accounting professionals saving time with ReceiptsAI
                      </p>
                      <PricingTestimonialsMarquee />
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
} 